import React, { useState } from 'react';
import ShareResultsModal from './ShareResultsModal';

const ArchivedGameScreen = ({
    players,
    numDecks,
    handHistory,
    gameState,
    onBack,
    gameConfig
}) => {
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);

    const winner = gameState === 'finished'
        ? players.reduce((max, player) =>
            player.score > (max?.score || -Infinity) ? player : max, null)
        : null;

    return (
        <div className="h-screen w-full bg-[#1a2332] font-['Inter'] overflow-hidden flex flex-col relative selection:bg-amber-500/30">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-5 pointer-events-none">
                <div className="absolute top-0 left-0 w-full h-full"
                    style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 5 C 35 15 45 15 45 25 C 45 35 30 50 30 50 C 30 50 15 35 15 25 C 15 15 25 15 30 5 Z' fill='%23ffffff' fill-rule='evenodd'/%3E%3C/svg%3E")`,
                        backgroundSize: '60px 60px'
                    }}
                />
            </div>

            {/* Ambient Glow */}
            <div className="absolute top-0 left-1/4 w-[50vw] h-[50vw] bg-purple-500/10 rounded-full blur-[100px] -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
            <div className="absolute bottom-0 right-1/4 w-[50vw] h-[50vw] bg-blue-500/10 rounded-full blur-[100px] translate-x-1/2 translate-y-1/2 pointer-events-none" />

            {/* Header */}
            <header className="relative z-20 flex items-center justify-between px-4 py-3 bg-[#1a2332]/80 backdrop-blur-md border-b border-white/5 shadow-lg">
                <div className="flex items-center gap-2">
                    <button
                        onClick={onBack}
                        className="group flex items-center justify-center w-10 h-10 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-amber-500/30 transition-all duration-200"
                        title="Back"
                    >
                        <svg className="w-5 h-5 text-gray-400 group-hover:text-amber-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                    </button>
                </div>

                <div className="flex flex-col items-center">
                    <h1 className="text-xl sm:text-2xl font-black tracking-tight font-['Roboto_Slab'] text-transparent bg-clip-text bg-gradient-to-r from-amber-200 to-yellow-400 drop-shadow-sm uppercase">
                        Archived Scorecard
                    </h1>
                </div>

                <div className="flex items-center gap-2">
                    {gameState === 'finished' && (
                        <button
                            onClick={() => setIsShareModalOpen(true)}
                            className="group flex items-center justify-center w-10 h-10 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-amber-500/30 transition-all duration-200"
                            title="Share Results"
                        >
                            <svg className="w-5 h-5 text-amber-400 group-hover:text-amber-300 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>
                        </button>
                    )}
                </div>
            </header>

            {/* Main Content */}
            <main className="relative z-10 flex-1 overflow-hidden flex flex-col">
                {gameState === 'finished' && winner && (
                    <div className="px-4 py-8 text-center bg-gradient-to-b from-amber-500/10 to-transparent">
                        <h2 className="text-sm font-bold uppercase tracking-widest text-amber-500 mb-2">Winner</h2>
                        <p
                            className="text-5xl font-black tracking-tighter drop-shadow-lg mb-2"
                            style={{ color: winner.color }}
                        >
                            {winner.name}
                        </p>
                        <p className="text-sm text-gray-400">
                            Total Score: <span className="text-white font-bold">{winner.score}</span>
                        </p>
                    </div>
                )}

                <div className="flex-1 overflow-auto">
                    <table className="w-full border-collapse text-left">
                        <thead className="sticky top-0 z-10 bg-[#1a2332] shadow-lg">
                            <tr>
                                <th className="sticky left-0 z-20 bg-[#1a2332] border-b border-r border-white/5 p-3 text-center min-w-[60px]">
                                    <span className="text-xs font-bold uppercase text-gray-500">#</span>
                                </th>
                                {players.map((player, i) => (
                                    <th key={i} className="border-b border-r border-white/5 bg-[#1a2332] p-3 min-w-[160px]">
                                        <div className="flex items-center gap-2 mb-2">
                                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: player.color }} />
                                            <span className="font-bold text-sm text-white">{player.name}</span>
                                        </div>
                                        <div className="grid grid-cols-4 gap-1 text-[10px] font-bold uppercase text-gray-500">
                                            <div>Bid</div>
                                            <div>Won</div>
                                            <div>Pts</div>
                                            <div>Tot</div>
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {Array.from({ length: 13 }, (_, handNum) => {
                                const hand = handHistory.find(h => h.handNumber === handNum + 1);

                                return (
                                    <tr key={handNum} className="hover:bg-white/[0.02]">
                                        <td className="sticky left-0 z-10 border-b border-r border-white/5 p-3 text-center font-mono text-sm bg-[#1a2332] text-gray-500">
                                            {handNum + 1}
                                        </td>
                                        {players.map((player, i) => {
                                            const playerData = hand?.players[player.name];
                                            const displayBid = playerData?.bid === 'nil' ? 'N' : playerData?.bid === 'blind-nil' ? 'BN' : playerData?.bid;

                                            return (
                                                <td key={i} className="border-b border-r border-white/5 p-3">
                                                    <div className="grid grid-cols-4 text-sm">
                                                        <div className="text-gray-400">{playerData ? displayBid : '-'}</div>
                                                        <div className="text-gray-400">{playerData ? playerData.won : '-'}</div>
                                                        <div className={`${playerData?.score < 0 ? 'text-red-400' : 'text-gray-300'}`}>
                                                            {playerData ? playerData.score : '-'}
                                                        </div>
                                                        <div className={`font-bold ${playerData?.total < 0 ? 'text-red-400' : 'text-white'}`}>
                                                            {playerData ? playerData.total : '-'}
                                                        </div>
                                                    </div>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </main>

            {/* Footer - Player Pods */}
            <footer className="relative z-20 bg-[#131b26] border-t border-white/5 pb-safe pt-3 px-4">
                <div className="flex items-center gap-3 overflow-x-auto pb-4 no-scrollbar">
                    {players.map((player, i) => (
                        <div
                            key={i}
                            className={`flex-shrink-0 flex flex-col p-3 rounded-xl min-w-[100px] border ${gameState === 'finished' && winner?.name === player.name
                                ? 'bg-amber-500/10 border-amber-500/50'
                                : 'bg-white/5 border-white/5'
                                }`}
                        >
                            <span className="text-xs font-bold truncate mb-1" style={{ color: player.color }}>
                                {player.name}
                            </span>
                            <span className={`text-2xl font-black ${player.score < 0 ? 'text-red-400' : 'text-white'}`}>
                                {player.score}
                            </span>
                            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
                                {player.bags} Bag{player.bags !== 1 ? 's' : ''}
                            </span>
                        </div>
                    ))}
                </div>
            </footer>

            <ShareResultsModal
                isOpen={isShareModalOpen}
                onClose={() => setIsShareModalOpen(false)}
                players={players}
                handHistory={handHistory}
                gameConfig={gameConfig}
            />
        </div>
    );
};

export default ArchivedGameScreen;
